import React, { useContext, useState } from 'react';
import {
  Box,
  Text,
  VStack,
  Grid,
  Flex,
  useToast,
  Spinner,
} from '@chakra-ui/react';
import { CheckIcon } from '@chakra-ui/icons';
import { AssociatedTagsContext } from '../../../contexts/AssociatedTagsContext';
import { influencerApi } from 'services/api/influencer';
import { InfluencerDataContext } from 'contexts/InfluencerDataContext';
import { ff } from '../../../theme';

interface NichesEditProps {
  selectedTags: string[];
  setSelectedTags: (tags: string[]) => void;
  collabPreference: string;
  lastSavedNichesInfo: {
    selectedTags: string[];
  } | null;
  onNichesSaved: (tags: string[]) => void;
  onBack: () => void;
}

// Emoji mapping for each niche
const nicheEmojis: { [key: string]: string } = {
    'Gaming': '🎮',
    'DIY': '🔨',
    'Fashion': '👗',
    'TikTok Shop': '🛍️',
    'Coffee': '☕',
    'Alcohol': '🍸',
    'Dance': '💃',
    'Business': '💼',
    'Travel': '✈️',
    'Athlete': '🏃',
    'Wellness': '🧘',
    'Sports': '⚽',
    'Fitness': '💪',
    'Education': '📚',
    'Photography': '📷',
    'Art': '🎨',
    'Food': '🍽️',
    'Music': '🎵',
    'Lifestyle': '✨',
    'Family': '👨‍👩‍👧‍👦',
    'Pets': '🐾',
    'Technology': '💻',
    'Comedy': '😂',
    'Beauty': '💄',
  };

const NichesEdit: React.FC<NichesEditProps> = ({
  selectedTags,
  setSelectedTags,
  collabPreference,
  lastSavedNichesInfo,
  onNichesSaved,
  onBack,
}) => {
  const toast = useToast();
  const { influencerData } = useContext(InfluencerDataContext);
  const { associatedTags: associatedTagsContext } = useContext(AssociatedTagsContext);
  const [savingTagId, setSavingTagId] = useState<string | null>(null);

  // Convert associatedTagsContext object to array
  const availableTags = Object.keys(associatedTagsContext).map(tagId => ({
    id: tagId,
    name: associatedTagsContext[tagId].tag,
  }));

  console.log('availableTags', availableTags);

  /**
   * Autosave niches to backend
   */
  const autosaveTags = async (updatedTags: string[], clickedTagId: string) => {
    if (!lastSavedNichesInfo || !influencerData?.id) return;
    
    try {
      setSavingTagId(clickedTagId);
      
      await influencerApi.updateProfile({
        collabPreference,
        associatedTags: updatedTags
      }, influencerData.id);
      
      // Update last saved value on success
      onNichesSaved(updatedTags);
    } catch (error) {
      console.error('Error autosaving niches:', error);
      // Revert to last saved value
      setSelectedTags(lastSavedNichesInfo.selectedTags);
      toast({
        title: "Couldn't save niches",
        description: "Reverted to last saved value",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setSavingTagId(null);
    }
  };

  const toggleTag = async (tagId: string) => {
    const updatedTags = selectedTags.includes(tagId)
      ? selectedTags.filter(id => id !== tagId)
      : [...selectedTags, tagId];
    
    setSelectedTags(updatedTags);
    
    // Autosave if tags actually changed
    if (lastSavedNichesInfo) {
      const lastSavedTagsStr = [...lastSavedNichesInfo.selectedTags].sort().join(',');
      const currentTagsStr = [...updatedTags].sort().join(',');
      if (lastSavedTagsStr !== currentTagsStr) {
        await autosaveTags(updatedTags, tagId);
      }
    }
  };

  return (
    <Box>
      {/* Description */}
      <Text color={ff.text2} fontSize="14px" mb={5}>
        Select the types of niches you put yourself in
      </Text>

      {/* Niches Grid */}
      <Grid templateColumns="repeat(2, 1fr)" gap={3}>
        {availableTags.map((tag) => {
          const isSelected = selectedTags.includes(tag.id);
          const isSaving = savingTagId === tag.id;
          const emoji = nicheEmojis[tag.name] || '🍽️'; // Default emoji if not found

          return (
            <Box
              key={tag.id}
              position="relative"
              bg={isSelected ? ff.limeSurface : ff.grey}
              border="1px solid"
              borderColor={isSelected ? ff.ink : 'transparent'}
              borderRadius="20px"
              px={4}
              py={4}
              cursor={isSaving ? 'not-allowed' : 'pointer'}
              onClick={() => !isSaving && toggleTag(tag.id)}
              transition="all 0.2s"
              _hover={{ borderColor: isSaving ? undefined : ff.ink }}
            >
              {/* Loading Overlay */}
              {isSaving && (
                <Flex
                  position="absolute"
                  top={0}
                  left={0}
                  right={0}
                  bottom={0}
                  bg="rgba(255, 255, 255, 0.8)"
                  borderRadius="20px"
                  align="center"
                  justify="center"
                  zIndex={2}
                >
                  <Spinner size="md" color={ff.ink} thickness="3px" />
                </Flex>
              )}
              
              {/* Check Icon */}
              {isSelected && !isSaving && (
                <Flex
                  position="absolute"
                  top={3}
                  right={3}
                  bg={ff.ink}
                  borderRadius="full"
                  w="24px"
                  h="24px"
                  align="center"
                  justify="center"
                >
                  <CheckIcon color={ff.lime} boxSize={3} />
                </Flex>
              )}

              {/* Emoji and Label */}
              <VStack spacing={2}>
                <Text fontSize="3xl">{emoji}</Text>
                <Text fontWeight={600} fontSize="14px" color={ff.ink} textAlign="center">
                  {tag.name}
                </Text>
              </VStack>
            </Box>
          );
        })}
      </Grid>
    </Box>
  );
};

export default NichesEdit;